import { computed, defineStore, reactive, useNuxtApp } from "#imports";
import useOrganizationsApi, {
  type TOrganizationsResponse,
  type TOrganization,
} from "@/composables/advisor/organizations-api";

interface TLibOrganizationStoreState {
  _organization: TOrganization | null;
  _organizationsLoading: boolean;
  _organizations: TOrganization[];
}

const initialState = {
  _organization: null,
  _organizationsLoading: false,
  _organizations: [],
} as TLibOrganizationStoreState;

const createStore = defineStore("libOrganizationStore", () => {
  const state = reactive({
    ...initialState,
  });

  const api = useOrganizationsApi();
  const { $notifyError } = useNuxtApp();

  const organization = computed(() => state._organization);
  const organizations = computed(() => state._organizations);
  const organizationsLoading = computed(() => state._organizationsLoading);
  const organizationName = computed(() => state._organization?.name ?? "");

  // Setters/Mutators

  function setOrganizations(orgs: TOrganization[]) {
    state._organizations = orgs;
  }
  function setOrganizationsLoading(data: boolean) {
    state._organizationsLoading = data;
  }
  function setSelectedOrganization(org: TOrganization | null) {
    state._organization = org;
  }
  function clear() {
    Object.assign(state, {
      ...initialState,
    });
  }

  // Actions

  function fetchOrganizations(force = false) {
    if (!force && organizations.value.length > 0) {
      return Promise.resolve(organizations.value);
    }
    setOrganizationsLoading(true);
    return api.getOrganizations().then(
      (resp: TOrganizationsResponse) => {
        setOrganizations(resp.items ?? []);
        if (organization.value) {
          const updatedOrganization = resp.items?.find(({ public_id }: TOrganization) => organization.value?.public_id === public_id);
          if (updatedOrganization) {
            setSelectedOrganization(updatedOrganization);
          }
        }
        setOrganizationsLoading(false);
        return resp?.items;
      },
      (error: Error) => {
        setOrganizationsLoading(false);
        $notifyError({
          text: `Get Organizations list. ${error}`,
        });
      },
    );
  }

  return {
    organization,
    organizations,
    organizationsLoading,
    organizationName,

    setOrganizations,
    setOrganizationsLoading,
    setSelectedOrganization,
    clear,

    fetchOrganizations,
  };
});

export default createStore;
